import { ENV } from '@forwardflow/helpers/configs'
import { useGetUserSubscription } from '@forwardflow/hooks/customHooks'
import SubscriptionService from '@forwardflow/services/SubscriptionService'
import { useUpdatePaymentModalStore } from '@forwardflow/store/modal/updatePaymentModal'
import { Button } from '@forwardflow/ui/Button'
import { TextField } from '@forwardflow/ui/TextField'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Form, message } from 'antd'
import { useState } from 'react'

export type ICardInformation = {
   nameOnCard: string
   country: string
   addressLine1: string
   city: string
   postalCode: string
}

type Package = {
   packageId: string
}

export const UpdatePaymentForm: React.FC<Package> = ({ packageId }) => {
   const [isLoading, setLoading] = useState<boolean>(false)
   const [cardError, setCardError] = useState('')
   const [form] = Form.useForm<ICardInformation>()
   const { closeUpdatePaymentModal } = useUpdatePaymentModalStore()
   const { mutate } = useGetUserSubscription()
   const stripe = useStripe()
   const elements = useElements()
   const handleSubmit = async () => {
      if (!stripe || !elements || !elements?.getElement(CardElement)) {
         // Stripe.js has not yet loaded.
         // Make sure to disable form submission until Stripe.js has loaded.
         return
      }

      try {
         setLoading(true)
         const values = await form.validateFields()
         const { error, paymentMethod } = await stripe.createPaymentMethod({
            elements: elements,
            params: {
               billing_details: {
                  name: values.nameOnCard,
                  address: {
                     city: values?.city,
                     country: values?.country,
                     line1: values?.addressLine1,
                     postal_code: values?.postalCode,
                  },
               },
            },
         })

         if (error) {
            setLoading(false)
            message.error('Error creating payment method')
            setCardError('Please enter valid card details')
            return
         }

         setCardError('')
         await SubscriptionService.subscribeToPackage(packageId, {
            id:
               ENV.NODE_ENV === 'development'
                  ? 'pm_card_visa'
                  : paymentMethod.id,
            name: paymentMethod.billing_details.name,
            country: paymentMethod?.billing_details?.address?.country || 'US',
            address: paymentMethod?.billing_details.address?.line1,
            city: paymentMethod?.billing_details.address?.city,
            postalCode: paymentMethod?.billing_details.address?.postal_code,
         })
         closeUpdatePaymentModal()
         message.success('Payment method updated successfully')
         mutate()
      } catch (error) {
         throw new Error('Error creating payment method')
      } finally {
         setLoading(false)
      }
   }

   return (
      <>
         <Form
            layout="vertical"
            form={form}
            initialValues={{}}
            onFinish={handleSubmit}
            className="text-left text-white"
            autoComplete="off"
         >
            <div>Card Information</div>
            <CardElement
               className="my-2 border border-white/10 rounded-xl !bg-[#222327] h-[50px] py-4 px-[22px] mb-4"
               options={{
                  style: {
                     base: {
                        fontSize: '14px',
                        color: 'white',
                        '::placeholder': {
                           color: '#818181',
                        },
                     },
                     invalid: {
                        color: '#9e2146',
                     },
                  },
                  hidePostalCode: true,
               }}
            />
            {cardError ? (
               <>
                  <div className="mb-2 -mt-2 text-red-500">{cardError}</div>
               </>
            ) : null}

            <TextField
               name="nameOnCard"
               isRequired
               errorMessage="Please enter name on the card"
               placeholder="Name on Card"
               className="!bg-[#222327]"
            />
            <div className="mt-2 mb-2">Billing Address</div>

            {/* <SelectField
          name="country"
          isRequired
          errorMessage="Please choose country"
          placeholder="Country"
          options={countries}
        /> */}

            <TextField
               name="addressLine1"
               isRequired
               errorMessage="Please enter address"
               placeholder="Address"
               className="!bg-[#222327]"
            />
            <div className="flex gap-x-5">
               <div className="flex-1">
                  <TextField
                     name="city"
                     isRequired
                     errorMessage="Please enter city"
                     placeholder="City"
                     className="!bg-[#222327]"
                  />
               </div>
               <div className="flex-1">
                  <TextField
                     isRequired
                     name="postalCode"
                     errorMessage="Please enter postal code"
                     placeholder="Postal code"
                     className="!bg-[#222327]"
                     validatePostalCode
                  />
               </div>
            </div>

            <p className="text-[12px] mb-5">
               Terms and conditions: dolor sit amet, consectetur adipiscing
               elit. Quisque condimentum maximus sapien sit amet hendrerit.
               Quisque porttitor, sapien eget laoreet auctor, magna nisl maximus
               mi, ac sollicitudin velit risus ut lectus. Mauris eu sagittis
               elit. Praesent euismod tempus quam a auctor. Mauris ac orci
               suscipit, rhoncus lacus sit amet, auctor ante. Morbi vestibulum
               risus leo
            </p>
            <Button
               isLoading={isLoading}
               className="w-full text-white !rounded"
               type="submit"
            >
               Update Payment Info
            </Button>
         </Form>
      </>
   )
}
